import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Grid,
  ImageList,
  ImageListItem,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
} from "@mui/material";
import {
  Close,
  ReceiptLong,
  Timeline,
  Person,
  LocationOn,
  Build,
  Payment,
  PhotoLibrary,
  PhotoCamera,
  WhatsApp,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

// Reusable DetailItem component
const DetailItem = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500}>
      {value || "-"}
    </Typography>
  </Box>
);

const ViewTicketModal = ({ open, handleClose, ticket }) => {
  const theme = useTheme();

  // Date formatting functions
  const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
  const formatShort = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  // Status determination
  const getTicketStatus = (ticket) => {
    if (!ticket) return "PENDING";
    if (ticket.ticket_status === "CLOSED") return "CLOSED";
    if (ticket.ticket_status === "CANCELED") return "CANCELED";
    if (
      ticket.ticket_status === "PENDING" &&
      ticket.broadcast_status === "matched_in_same_center"
    ) {
      return ticket.is_technician_assigned
        ? "TECHNICIAN ASSIGNED"
        : "TECHNICIAN NOT ASSIGNED";
    }
    if (ticket.ticket_status === "ACCEPTED") {
      return ticket.is_technician_assigned
        ? "TECHNICIAN ASSIGNED"
        : "TECHNICIAN NOT ASSIGNED";
    }
    return ticket.ticket_status || "PENDING";
  };

  // Timeline steps builder
  const buildSteps = () => {
    if (!ticket) return [];
    const {
      ticket_status,
      createdAt,
      acceptedAt,
      assignedAt,
      closedAt,
      canceledAt,
      is_technician_assigned,
    } = ticket;

    const status = getTicketStatus(ticket);
    const isCanceled = status === "CANCELED";
    const isClosed = status === "CLOSED";
    const isTechnicianAssigned = status.includes("ASSIGNED");
    const isPending = status === "PENDING";

    return [
      {
        id: "created",
        label: "Request Created",
        description: "Your service request has been received",
        time: createdAt,
        completed: true,
        active: true,
        icon: Timeline,
      },
      {
        id: "accepted",
        label: "Request Accepted",
        description: isTechnicianAssigned
          ? "Technician assigned and on the way"
          : "Looking for available technician",
        time: acceptedAt,
        completed: !!acceptedAt,
        active: !!acceptedAt || isCanceled || isClosed,
        icon: Person,
      },
      {
        id: "assigned",
        label: "Technician Assigned",
        description: isTechnicianAssigned
          ? `${
              ticket.assigned_ids?.assigned_technician_id?.name || "Technician"
            } assigned`
          : "Awaiting technician assignment",
        time: assignedAt,
        completed: is_technician_assigned,
        active: is_technician_assigned || isClosed || isCanceled,
        icon: Build,
      },
      {
        id: "completed",
        label: isCanceled ? "Request Canceled" : "Request Completed",
        description: isCanceled
          ? "Service request was canceled"
          : "Service successfully completed",
        time: isCanceled ? canceledAt : closedAt,
        completed: isClosed || isCanceled,
        active: isClosed || isCanceled,
        icon: isCanceled ? Close : ReceiptLong,
      },
    ].filter((step) => !(step.id === "assigned" && isCanceled)); // Hide assigned step if canceled
  };

  // WhatsApp integration
  const handleWhatsAppClick = (number, name) => {
    if (number) {
      const formattedPhoneNumber = number.replace(/[^\d]/g, "");
      const message = `Hi ${name}, regarding your service request ${ticket.ticket_id}`;
      const encodedMessage = encodeURIComponent(message);
      window.open(
        `https://wa.me/${formattedPhoneNumber}?text=${encodedMessage}`,
        "_blank"
      );
    }
  };

  // Status color mapping
  const statusColors = {
    CLOSED: "#0d9488",
    CANCELED: "#dc2626",
    "Technician Assigned": "#0d9488",
    "Technician Not Assigned": "#dc2626",
    PENDING: "#ea580c",
    ACCEPTED: "#0d9488",
  };

  if (!ticket) return null;

  const steps = buildSteps();
  const status = getTicketStatus(ticket);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#0d9488",
          color: theme.palette.common.white,
          py: 2,
          px: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          <ReceiptLong sx={{ mr: 1.5 }} />
          <Typography variant="h6" fontWeight="600">
            Service Request: {ticket.ticket_id}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: theme.palette.common.white }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Status Banner */}
        <Box
          sx={{
            backgroundColor: theme.palette.grey[100],
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Current Status
              </Typography>
              <Box display="flex" alignItems="center" mt={0.5} gap={1.5}>
                <Chip
                  label={status}
                  sx={{
                    backgroundColor:
                      statusColors[status] || theme.palette.grey[500],
                    color: "white",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                />
              </Box>
            </Box>
            <Chip
              label={`Created: ${formatShort(ticket.createdAt)}`}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </Box>

        {/* Modern Timeline */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="600" mb={2}>
            <Timeline sx={{ verticalAlign: "middle", mr: 1 }} />
            Request Progress
          </Typography>

          <Stepper
            orientation="vertical"
            activeStep={steps.filter((s) => s.completed).length - 1}
          >
            {steps.map((step, index) => (
              <Step
                key={step.id}
                completed={step.completed}
                active={step.active}
              >
                <StepLabel
                  optional={
                    step.time ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatShort(step.time)}
                      </Typography>
                    ) : null
                  }
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: step.completed
                          ? "#0d9488"
                          : step.active
                          ? "#0f766e"
                          : theme.palette.grey[300],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: step.completed
                          ? theme.palette.common.white
                          : theme.palette.text.primary,
                        mr: 2,
                      }}
                    >
                      <step.icon fontSize="small" />
                    </Box>
                  )}
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: 600,
                      color: step.active
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                    },
                  }}
                >
                  <Box>
                    <Typography fontWeight={600}>{step.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Customer Information */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight="600">
              <Person sx={{ verticalAlign: "middle", mr: 1 }} />
              Customer Details
            </Typography>
            <Box>
              <Tooltip title="Contact via WhatsApp">
                <IconButton
                  onClick={() =>
                    handleWhatsAppClick(
                      ticket?.personal_details?.alternate_phone
                        ?.mobile_number ||
                        ticket?.personal_details?.primary_phone?.mobile_number,
                      ticket?.personal_details?.name
                    )
                  }
                  size="small"
                  sx={{
                    backgroundColor: "#25D366",
                    color: "white",
                    "&:hover": { backgroundColor: "#128C7E" },
                  }}
                >
                  <WhatsApp />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DetailItem label="Name" value={ticket.personal_details?.name} />
              <DetailItem
                label="Email"
                value={ticket.personal_details?.email}
              />
              <DetailItem
                label="Phone"
                value={ticket.personal_details?.primary_phone?.mobile_number}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailItem
                label="Service Category"
                value={ticket.service_provided_for?.service_name}
              />
              <DetailItem
                label="Preferred Time"
                value={ticket.time_preference?.time_preference_type}
              />
              {ticket.time_preference?.specific_date_time && (
                <DetailItem
                  label="Specific Time"
                  value={formatDate(ticket.time_preference.specific_date_time)}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Address Section */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="600" mb={2}>
            <LocationOn sx={{ verticalAlign: "middle", mr: 1 }} />
            Service Address
          </Typography>

          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography fontWeight={500}>
              {[
                ticket.address_details?.house_number,
                ticket.address_details?.locality,
                ticket.address_details?.city,
                ticket.address_details?.state,
                ticket.address_details?.country,
              ]
                .filter(Boolean)
                .join(", ")}
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Pincode: {ticket.address_details?.pincode || "-"}
            </Typography>
          </Box>
        </Box>

        {/* Service Details */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="600" mb={2}>
            <Build sx={{ verticalAlign: "middle", mr: 1 }} />
            Service Details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DetailItem
                label="Specific Requirements"
                value={ticket.specific_requirement || "None"}
              />
              <DetailItem
                label="Closed Code"
                value={ticket.remarks?.message || "-"}
              />
              <DetailItem
                label="Customer Rating"
                value={
                  ticket.remarks?.star_rating
                    ? `${ticket.remarks.star_rating}/5`
                    : "-"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailItem
                label="Technician Remarks"
                value={ticket.remarks?.technician_remarks || "-"}
              />
              <DetailItem
                label="Closing Remarks"
                value={ticket.remarks?.close_ticket_remarks || "-"}
              />
              <DetailItem
                label="Additional Remarks"
                value={
                  ticket.remarks?.additional_remarks?.length
                    ? ticket.remarks.additional_remarks.join(", ")
                    : "None"
                }
              />
            </Grid>
          </Grid>
        </Box>

        {/* Payment Details */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="600" mb={2}>
            <Payment sx={{ verticalAlign: "middle", mr: 1 }} />
            Payment Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DetailItem
                label="Payment Status"
                value={ticket.b2b_payment_details?.payment_status || "-"}
              />
              <DetailItem
                label="Final Amount"
                value={`₹${ticket.closing_ticket_price || "0"}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailItem
                label="Payment Mode"
                value={ticket.payment_type || "-"}
              />
              <DetailItem
                label="Tracking Link"
                value={
                  <Link
                    href={`https://app.onit.services/#/ServiceStatus/?${ticket?._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 500, color: "#0d9488" }}
                  >
                    View Service Tracking
                  </Link>
                }
              />
            </Grid>
          </Grid>
        </Box>

        {/* Image Galleries */}
        {ticket.onsite_pictures?.length > 0 && (
          <Box
            sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Typography variant="h6" fontWeight="600" mb={2}>
              <PhotoLibrary sx={{ verticalAlign: "middle", mr: 1 }} />
              Service Photos
            </Typography>
            <ImageList cols={3} gap={12}>
              {ticket.onsite_pictures.map((src, i) => (
                <ImageListItem key={i}>
                  <img
                    src={src}
                    alt={`Service photo ${i + 1}`}
                    style={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                    loading="lazy"
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}

        {ticket.client_onsite_pictures?.length > 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" mb={2}>
              <PhotoCamera sx={{ verticalAlign: "middle", mr: 1 }} />
              Uploaded Pictures
            </Typography>
            <ImageList cols={3} gap={12}>
              {ticket.client_onsite_pictures.map((src, i) => (
                <ImageListItem key={i}>
                  <img
                    src={src}
                    alt={`Customer photo ${i + 1}`}
                    style={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                    loading="lazy"
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: "#0d9488",
            borderRadius: 2,
            px: 3,
            py: 1,
            "&:hover": {
              backgroundColor: "#0f766e",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewTicketModal;
